import { Document, Page, Text, View, StyleSheet, Link } from '@react-pdf/renderer'
import type { Resume, ResumeSectionKey } from '../../../types'
import {
  contactInline,
  joinDateRange,
  getSectionOrder,
  labelFor,
  displayUrl,
  BULLET,
  pdfMetadata,
  pdfFontFamily,
  previewFontFamily,
  getAccent,
  type PdfFontFamily,
} from './shared'

/**
 * Minimal template — generous whitespace, no decoration. Section labels are
 * small, lowercase-cap, and color-muted. Accent color used only for hyperlinks.
 */

const makeStyles = (font: PdfFontFamily, accent: string) =>
  StyleSheet.create({
    page: { paddingTop: 56, paddingBottom: 56, paddingHorizontal: 64, fontSize: 10.5, color: '#1f2937', fontFamily: font.regular, lineHeight: 1.5 },
    headerName: { fontSize: 22, fontFamily: font.bold, color: '#0f172a' },
    headerHeadline: { fontSize: 11, color: '#475569', marginTop: 2 },
    headerContacts: { fontSize: 9.5, color: '#64748b', marginTop: 8 },
    headerSpacer: { height: 18 },
    sectionTitle: { fontSize: 9.5, fontFamily: font.bold, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 6 },
    section: { marginBottom: 16 },
    itemHeader: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 1 },
    itemTitle: { fontSize: 11, fontFamily: font.bold, color: '#0f172a' },
    itemMeta: { fontSize: 9.5, color: '#64748b' },
    bulletRow: { flexDirection: 'row', marginTop: 2 },
    bulletDot: { width: 10, fontSize: 10.5, color: '#94a3b8' },
    bulletText: { flex: 1, fontSize: 10.5, lineHeight: 1.5 },
    paragraph: { fontSize: 10.5, lineHeight: 1.55 },
    pageNumber: { position: 'absolute', bottom: 28, left: 0, right: 0, textAlign: 'center', fontSize: 8.5, color: '#cbd5e1' },
    linkInline: { textDecoration: 'none', color: accent },
  })

type MinimalStyles = ReturnType<typeof makeStyles>

function renderSection(key: ResumeSectionKey, resume: Resume, styles: MinimalStyles, font: PdfFontFamily) {
  switch (key) {
    case 'summary':
      if (!resume.summary) return null
      return (
        <View key={key} style={styles.section} wrap={false}>
          <Text style={styles.sectionTitle}>{labelFor(resume, 'summary')}</Text>
          <Text style={styles.paragraph}>{resume.summary}</Text>
        </View>
      )
    case 'experience':
      if (resume.experience.length === 0) return null
      return (
        <View key={key} style={styles.section}>
          <Text style={styles.sectionTitle}>{labelFor(resume, 'experience')}</Text>
          {resume.experience.map((exp) => (
            <View key={exp.id} style={{ marginBottom: 8 }} wrap={false}>
              <View style={styles.itemHeader}>
                <Text style={styles.itemTitle}>{exp.role || exp.company}</Text>
                <Text style={styles.itemMeta}>{joinDateRange(exp.startDate, exp.endDate)}</Text>
              </View>
              <Text style={styles.itemMeta}>{[exp.company, exp.location].filter(Boolean).join(' · ')}</Text>
              {exp.bullets.filter(Boolean).map((b, i) => (
                <View key={i} style={styles.bulletRow}>
                  <Text style={styles.bulletDot}>{BULLET}</Text>
                  <Text style={styles.bulletText}>{b}</Text>
                </View>
              ))}
            </View>
          ))}
        </View>
      )
    case 'education':
      if (resume.education.length === 0) return null
      return (
        <View key={key} style={styles.section}>
          <Text style={styles.sectionTitle}>{labelFor(resume, 'education')}</Text>
          {resume.education.map((e) => (
            <View key={e.id} style={{ marginBottom: 5 }} wrap={false}>
              <View style={styles.itemHeader}>
                <Text style={styles.itemTitle}>{e.institution}</Text>
                <Text style={styles.itemMeta}>{joinDateRange(e.startDate, e.endDate)}</Text>
              </View>
              <Text style={styles.itemMeta}>{[e.degree, e.field, e.location].filter(Boolean).join(' · ')}</Text>
              {e.details ? <Text style={styles.paragraph}>{e.details}</Text> : null}
            </View>
          ))}
        </View>
      )
    case 'skills':
      if (resume.skills.length === 0) return null
      return (
        <View key={key} style={styles.section} wrap={false}>
          <Text style={styles.sectionTitle}>{labelFor(resume, 'skills')}</Text>
          {resume.skills.map((g) => (
            <Text key={g.id} style={styles.paragraph}>
              <Text style={{ fontFamily: font.bold }}>{g.category} </Text>
              {g.items.join(', ')}
            </Text>
          ))}
        </View>
      )
    case 'projects':
      if (resume.projects.length === 0) return null
      return (
        <View key={key} style={styles.section}>
          <Text style={styles.sectionTitle}>{labelFor(resume, 'projects')}</Text>
          {resume.projects.map((p) => (
            <View key={p.id} style={{ marginBottom: 5 }} wrap={false}>
              <View style={styles.itemHeader}>
                <Text style={styles.itemTitle}>{p.name}</Text>
                {p.url ? <Link src={p.url} style={[styles.itemMeta, styles.linkInline]}>{displayUrl(p.url)}</Link> : null}
              </View>
              {p.description ? <Text style={styles.paragraph}>{p.description}</Text> : null}
              {p.bullets.filter(Boolean).map((b, i) => (
                <View key={i} style={styles.bulletRow}>
                  <Text style={styles.bulletDot}>{BULLET}</Text>
                  <Text style={styles.bulletText}>{b}</Text>
                </View>
              ))}
            </View>
          ))}
        </View>
      )
    case 'certifications':
      if (resume.certifications.length === 0) return null
      return (
        <View key={key} style={styles.section} wrap={false}>
          <Text style={styles.sectionTitle}>{labelFor(resume, 'certifications')}</Text>
          {resume.certifications.map((c) => (
            <Text key={c.id} style={styles.paragraph}>{c.name}{c.issuer ? ` · ${c.issuer}` : ''}{c.date ? ` · ${c.date}` : ''}</Text>
          ))}
        </View>
      )
    case 'languages':
      if (resume.languages.length === 0) return null
      return (
        <View key={key} style={styles.section} wrap={false}>
          <Text style={styles.sectionTitle}>{labelFor(resume, 'languages')}</Text>
          <Text style={styles.paragraph}>{resume.languages.map((l) => l.proficiency ? `${l.name} (${l.proficiency})` : l.name).join(' · ')}</Text>
        </View>
      )
    case 'customSections':
      return resume.customSections.map((s) => (
        <View key={s.id} style={styles.section} wrap={false}>
          <Text style={styles.sectionTitle}>{s.title}</Text>
          <Text style={styles.paragraph}>{s.body}</Text>
        </View>
      ))
  }
}

export function MinimalPdf({ resume }: { resume: Resume }) {
  const contacts = contactInline(resume.contact)
  const meta = pdfMetadata(resume)
  const order = getSectionOrder(resume)
  const font = pdfFontFamily(resume, 'sans')
  const accent = getAccent(resume)
  const styles = makeStyles(font, accent)
  return (
    <Document title={meta.title} author={meta.author} subject={meta.subject}>
      <Page size="A4" style={styles.page}>
        <View>
          <Text style={styles.headerName}>{resume.contact.fullName || ' '}</Text>
          {resume.contact.headline ? <Text style={styles.headerHeadline}>{resume.contact.headline}</Text> : null}
          {contacts.length > 0 && <Text style={styles.headerContacts}>{contacts.join('   ')}</Text>}
        </View>
        <View style={styles.headerSpacer} />

        {order.map((key) => renderSection(key, resume, styles, font))}

        <Text
          style={styles.pageNumber}
          fixed
          render={({ pageNumber, totalPages }) => (totalPages > 1 ? `${pageNumber} / ${totalPages}` : '')}
        />
      </Page>
    </Document>
  )
}

export function MinimalPreview({ resume }: { resume: Resume }) {
  const contacts = contactInline(resume.contact)
  const order = getSectionOrder(resume)
  const accent = getAccent(resume)
  return (
    <div className="bg-white text-slate-700 shadow-sm rounded-md px-16 py-14 text-[12px] leading-relaxed" style={{ fontFamily: previewFontFamily(resume, 'sans') }}>
      <div>
        <div className="text-[22px] font-bold text-slate-900">{resume.contact.fullName || '\u00a0'}</div>
        {resume.contact.headline && <div className="text-slate-600">{resume.contact.headline}</div>}
        {contacts.length > 0 && <div className="text-slate-500 text-[11px] mt-2">{contacts.join('   ')}</div>}
      </div>
      <div className="h-4" />

      {order.map((key) => {
        const Section = ({ children, title }: { children: React.ReactNode; title: string }) => (
          <div className="mb-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-1.5">{title}</div>
            <div>{children}</div>
          </div>
        )
        switch (key) {
          case 'summary':
            if (!resume.summary) return null
            return <Section key={key} title={labelFor(resume, 'summary')}><p>{resume.summary}</p></Section>
          case 'experience':
            if (resume.experience.length === 0) return null
            return (
              <Section key={key} title={labelFor(resume, 'experience')}>
                {resume.experience.map((e) => (
                  <div key={e.id} className="mb-2.5">
                    <div className="flex justify-between"><div className="font-bold text-slate-900">{e.role || e.company}</div><div className="text-slate-500 text-[11px]">{joinDateRange(e.startDate, e.endDate)}</div></div>
                    <div className="text-slate-500 text-[11px]">{[e.company, e.location].filter(Boolean).join(' · ')}</div>
                    <ul className="list-disc pl-5 mt-0.5 marker:text-slate-400">{e.bullets.filter(Boolean).map((b, i) => <li key={i}>{b}</li>)}</ul>
                  </div>
                ))}
              </Section>
            )
          case 'education':
            if (resume.education.length === 0) return null
            return (
              <Section key={key} title={labelFor(resume, 'education')}>
                {resume.education.map((e) => (
                  <div key={e.id} className="mb-1.5">
                    <div className="flex justify-between"><div className="font-bold text-slate-900">{e.institution}</div><div className="text-slate-500 text-[11px]">{joinDateRange(e.startDate, e.endDate)}</div></div>
                    <div className="text-slate-500 text-[11px]">{[e.degree, e.field, e.location].filter(Boolean).join(' · ')}</div>
                    {e.details && <p>{e.details}</p>}
                  </div>
                ))}
              </Section>
            )
          case 'skills':
            if (resume.skills.length === 0) return null
            return <Section key={key} title={labelFor(resume, 'skills')}>{resume.skills.map((g) => <p key={g.id}><b className="text-slate-900">{g.category}</b> {g.items.join(', ')}</p>)}</Section>
          case 'projects':
            if (resume.projects.length === 0) return null
            return (
              <Section key={key} title={labelFor(resume, 'projects')}>
                {resume.projects.map((p) => (
                  <div key={p.id} className="mb-1.5">
                    <div className="flex justify-between">
                      <div className="font-bold text-slate-900">{p.name}</div>
                      {p.url && <a className="text-[11px]" style={{ color: accent }} href={p.url}>{displayUrl(p.url)}</a>}
                    </div>
                    {p.description && <p>{p.description}</p>}
                    <ul className="list-disc pl-5 marker:text-slate-400">{p.bullets.filter(Boolean).map((b, i) => <li key={i}>{b}</li>)}</ul>
                  </div>
                ))}
              </Section>
            )
          case 'certifications':
            if (resume.certifications.length === 0) return null
            return <Section key={key} title={labelFor(resume, 'certifications')}>{resume.certifications.map((c) => <p key={c.id}>{c.name}{c.issuer ? ` · ${c.issuer}` : ''}{c.date ? ` · ${c.date}` : ''}</p>)}</Section>
          case 'languages':
            if (resume.languages.length === 0) return null
            return <Section key={key} title={labelFor(resume, 'languages')}><p>{resume.languages.map((l) => l.proficiency ? `${l.name} (${l.proficiency})` : l.name).join(' · ')}</p></Section>
          case 'customSections':
            return resume.customSections.map((s) => <Section key={s.id} title={s.title}><p className="whitespace-pre-line">{s.body}</p></Section>)
        }
      })}
    </div>
  )
}
